import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useCompliance, TSC_CATEGORIES, COMPLIANCE_FRAMEWORKS } from '../../contexts/ComplianceContext';
import TSCCategorySelector from '../../components/compliance/TSCCategorySelector';
import { Shield, Calendar, ArrowRight, Info, Loader } from 'lucide-react';

const ProjectSetupStep = () => {
  const {
    projectData,
    updateProjectData,
    toggleTSCCategory,
    createProject,
    loading,
    error,
  } = useCompliance();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!projectData.name.trim()) {
      return;
    }

    try {
      await createProject();
    } catch (err) {
      // Error is handled in context
      console.error('Failed to create project:', err);
    }
  };

  const selectedFramework = COMPLIANCE_FRAMEWORKS.find(
    f => f.id === projectData.frameworkId
  );

  // Calculate minimum date (today)
  const minDate = new Date();

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Project Name */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Project Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={projectData.name}
          onChange={(e) => updateProjectData({ name: e.target.value })}
          placeholder="e.g., Q1 2026 SOC 2 Audit"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
          required
        />
        <p className="mt-2 text-sm text-gray-500">
          Choose a descriptive name for your compliance project
        </p>
      </div>

      {/* Framework Selection */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Compliance Framework
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {COMPLIANCE_FRAMEWORKS.map((framework) => (
            <div
              key={framework.id}
              className={`
                relative p-4 rounded-lg border-2 transition-all
                ${framework.id === projectData.frameworkId
                  ? 'border-teal-600 bg-teal-50'
                  : framework.enabled
                    ? 'border-gray-200 hover:border-gray-300 cursor-pointer'
                    : 'border-gray-100 bg-gray-50 opacity-60'
                }
              `}
              onClick={() => {
                if (framework.enabled) {
                  updateProjectData({ frameworkId: framework.id });
                }
              }}
            >
              {framework.comingSoon && (
                <span className="absolute top-2 right-2 text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                  Coming Soon
                </span>
              )}

              <div className="flex items-center gap-3">
                <div
                  className={`
                    w-10 h-10 rounded-lg flex items-center justify-center
                    ${framework.id === projectData.frameworkId
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-600'
                    }
                  `}
                >
                  <Shield className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{framework.name}</p>
                  <p className="text-xs text-gray-500">Version {framework.version}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {selectedFramework && (
          <div className="mt-4 p-3 bg-teal-50 border border-teal-200 rounded-lg flex items-start gap-2">
            <Info className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-teal-800">
              {selectedFramework.name} evaluates the design of your security controls at a specific point in time.
            </p>
          </div>
        )}
      </div>

      {/* TSC Categories */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <TSCCategorySelector
          categories={TSC_CATEGORIES}
          selectedCategories={projectData.tscCategories}
          onToggle={toggleTSCCategory}
          disabled={loading}
        />
      </div>

      {/* Target Audit Date */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Target Audit Date
        </label>
        <p className="text-sm text-gray-500 mb-4">
          When do you expect your audit to take place? This helps prioritize tasks.
        </p>

        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-xs">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <DatePicker
              selected={projectData.targetAuditDate}
              onChange={(date) => updateProjectData({ targetAuditDate: date })}
              minDate={minDate}
              dateFormat="MMMM d, yyyy"
              placeholderText="Select a date"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
            />
          </div>

          {projectData.targetAuditDate && (
            <button
              type="button"
              onClick={() => updateProjectData({ targetAuditDate: null })}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading || !projectData.name.trim()}
          className={`
            flex items-center gap-2 px-6 py-3 bg-teal-600 text-white font-medium rounded-lg
            hover:bg-teal-700 transition-colors
            ${(loading || !projectData.name.trim()) ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {loading ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Creating Project...
            </>
          ) : (
            <>
              Continue to Integrations
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default ProjectSetupStep;
